import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Bed,
  User,
  ChevronRight,
  Loader2,
  Users,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { useOssAuth } from "../../context/OssAuthContext";
import OssSectionPageHeader from "../../components/oss/OssSectionPageHeader";
import OssTerapiaAlertBadge from "../../components/oss/OssTerapiaAlertBadge";
import { formatCameraAssegnata } from "../../utils/formatCamera";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDataNascita(s) {
  if (!s || !String(s).trim()) return "—";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleDateString("it-IT");
}

function getSessoAvatarStyles(sesso) {
  const raw = (sesso ?? "").toString().trim();
  const u = raw.toUpperCase();
  const lower = raw.toLowerCase();
  const femmina = u === "F" || u === "FEMMINA" || lower === "femmina";
  const maschio = u === "M" || u === "MASCHIO" || lower === "maschio";
  if (femmina) {
    return {
      fotoRing: "ring-2 ring-pink-400 ring-offset-2 border-transparent",
      placeholderBg: "bg-gradient-to-br from-pink-50 to-rose-100 border-pink-200/90",
      iconClass: "text-pink-600",
    };
  }
  if (maschio) {
    return {
      fotoRing: "ring-2 ring-blue-500 ring-offset-2 border-transparent",
      placeholderBg: "bg-gradient-to-br from-sky-50 to-blue-100 border-blue-200/90",
      iconClass: "text-blue-600",
    };
  }
  return {
    fotoRing: "border border-slate-200",
    placeholderBg: "bg-gradient-to-br from-slate-100 to-slate-200 border-slate-200/80",
    iconClass: "text-slate-500",
  };
}

export default function OssCameraDettaglio() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useOssAuth();
  const [camera, setCamera] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token || !id) return;
    setLoading(true);
    setError(null);
    axios
      .get(`${API_URL}/api/oss/me/camere/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => setCamera(data))
      .catch(() => {
        setCamera(null);
        setError("Camera non trovata o accesso negato");
      })
      .finally(() => setLoading(false));
  }, [token, id]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  if (error || !camera) {
    return (
      <div className="text-center py-16 text-slate-500 w-full">
        <Bed className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <p>{error || "Camera non trovata"}</p>
        <Button
          variant="outline"
          className="mt-4 min-h-[48px] touch-manipulation"
          onClick={() => navigate("/oss/camere")}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Torna alle camere
        </Button>
      </div>
    );
  }

  const label = formatCameraAssegnata(camera.name) || camera.name || "Camera";
  const ospiti = camera.ospiti || [];
  const notes = (camera.notes || "").trim();
  const subtitle = [
    camera.type,
    camera.piano ? `Piano ${camera.piano}` : null,
    notes || null,
    `${ospiti.length} ospit${ospiti.length === 1 ? "e" : "i"}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="w-full">
      <OssSectionPageHeader
        sectionKey="camere"
        backTo="/oss/camere"
        title={label}
        subtitle={subtitle}
        headerExtra={
          (Number(camera.alert_terapie) || 0) > 0 ? (
            <OssTerapiaAlertBadge
              count={camera.alert_terapie}
              overdue={camera.alert_terapie_overdue}
              size="md"
              label="da somministrare oggi"
            />
          ) : null
        }
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/oss/camere")}
              className="min-h-[44px] touch-manipulation bg-white/80"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tutte le camere
            </Button>
          </div>
        }
      />

      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-3 flex items-center gap-2">
        <Users className="w-4 h-4" />
        Ospiti in camera
      </h2>

      {ospiti.length === 0 ? (
        <div className="text-center py-12 text-slate-500 bg-white rounded-2xl border border-slate-200">
          <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <p>Nessun ospite assegnato a questa camera</p>
        </div>
      ) : (
        <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3" role="list">
          {ospiti.map((o) => {
            const av = getSessoAvatarStyles(o.sesso);
            return (
              <li key={o.id}>
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/oss/fascicoli/${o.id}`, { state: { fromCamera: id } })
                  }
                  className="group w-full flex items-center gap-4 sm:gap-5 oss-tile bg-white hover:border-teal-400 hover:bg-teal-50/40 transition-all p-4 sm:p-5 text-left touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 min-h-[96px]"
                >
                  {o.foto_url ? (
                    <img
                      src={o.foto_url}
                      alt=""
                      className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover shrink-0 ${av.fotoRing}`}
                    />
                  ) : (
                    <div
                      className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center shrink-0 border ${av.placeholderBg}`}
                    >
                      <User className={`w-8 h-8 sm:w-10 sm:h-10 ${av.iconClass}`} />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-slate-900 text-xl sm:text-2xl leading-tight truncate">
                      {o.cognome || o.nome_completo}
                      {o.nome ? (
                        <span className="font-semibold text-slate-700"> {o.nome}</span>
                      ) : null}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      <OssTerapiaAlertBadge
                        count={o.alert_terapie}
                        overdue={o.alert_terapie_overdue}
                      />
                      <span className="text-sm text-slate-500 tabular-nums">
                        {formatDataNascita(o.data_nascita)}
                      </span>
                      {o.diagnosi_principale ? (
                        <Badge
                          variant="outline"
                          className="text-xs font-normal max-w-full truncate border-slate-200"
                          title={o.diagnosi_principale}
                        >
                          {o.diagnosi_principale}
                        </Badge>
                      ) : null}
                    </div>
                    <p className="text-sm text-teal-700 font-medium mt-2 group-hover:underline">
                      Apri fascicolo
                    </p>
                  </div>
                  <ChevronRight className="w-6 h-6 text-slate-300 group-hover:text-teal-500 shrink-0" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
